/**
 * Generative-UI spike — the component catalog.
 *
 * The closed set of components an agent may emit. Tailwind + Lucide (no emojis).
 * Adding a capability = adding a catalog entry, not a new page. json-render would
 * supply its own catalog of ~41 components; this proves the pattern with a few.
 */
import React from 'react';
import { CheckCircle2, Circle, Loader2, LayoutGrid, FileText, Workflow, Presentation, Download, TrendingUp, TrendingDown, Minus, AlertTriangle, Eye } from 'lucide-react';
import {
  ResponsiveContainer, PieChart as RPie, Pie, Cell, Tooltip, Legend,
  LineChart as RLine, Line, BarChart as RBar, Bar, ScatterChart as RScatter, Scatter,
  XAxis, YAxis, CartesianGrid, ReferenceLine,
} from 'recharts';

export type ActionHandler = (action: string, value?: any) => void;

// Monochrome + THEME-AWARE: series use `currentColor` (the chart container's ink — dark in light
// mode, light in dark mode) at descending opacities, so charts stay grayscale AND readable in
// both themes (see CLAUDE.md → Color Palette). The ChartFrame sets the ink via a Tailwind class.
const RAMP = [1, 0.62, 0.4, 0.26, 0.82, 0.5];
const rampAt = (i: number) => RAMP[i % RAMP.length];

const toneText: Record<string, string> = {
  good: 'text-green-600 dark:text-green-400',
  warn: 'text-amber-600 dark:text-amber-400',
  bad: 'text-red-600 dark:text-red-400',
  default: 'text-secondary-500 dark:text-secondary-400',
};

const statusIcon: Record<string, React.ReactNode> = {
  done: <CheckCircle2 className="h-4 w-4 text-secondary-900 dark:text-white" />,
  in_progress: <Loader2 className="h-4 w-4 text-secondary-500 dark:text-secondary-400" />,
  todo: <Circle className="h-4 w-4 text-secondary-300 dark:text-secondary-600" />,
};

export const Stack: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-col gap-3">{children}</div>
);

export const Card: React.FC<{ title?: string; children?: React.ReactNode }> = ({ title, children }) => (
  <div className="rounded-xl border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-900 p-4 shadow-sm">
    {title && <h3 className="mb-3 text-sm font-semibold text-secondary-900 dark:text-white">{title}</h3>}
    <div className="flex flex-col gap-3">{children}</div>
  </div>
);

export const StatGroup: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-wrap gap-4">{children}</div>
);

export const Stat: React.FC<{ label: string; value: string; delta?: string; tone?: string }> = ({ label, value, delta, tone }) => (
  <div className="min-w-[120px]">
    <div className="text-xs text-secondary-500 dark:text-secondary-400">{label}</div>
    <div className="text-lg font-semibold text-secondary-900 dark:text-white">{value}</div>
    {delta && <div className={`text-xs ${toneText[tone || 'default']}`}>{delta}</div>}
  </div>
);

export const TaskList: React.FC<{ title?: string; items?: Array<{ title: string; status?: string }> }> = ({ title, items = [] }) => (
  <div>
    {title && <div className="mb-1 text-xs font-medium uppercase tracking-wide text-secondary-400">{title}</div>}
    <ul className="flex flex-col gap-1.5">
      {items.map((it, i) => (
        <li key={i} className="flex items-center gap-2 text-sm text-secondary-700 dark:text-secondary-200">
          {statusIcon[it.status || 'todo'] || statusIcon.todo}
          <span className={it.status === 'done' ? 'line-through text-secondary-400' : ''}>{it.title}</span>
        </li>
      ))}
    </ul>
  </div>
);

export const Heading: React.FC<{ text: string; children?: React.ReactNode }> = ({ text }) => (
  <h2 className="text-base font-semibold text-secondary-900 dark:text-white">{text}</h2>
);

export const Text: React.FC<{ text?: string; children?: React.ReactNode }> = ({ text, children }) => (
  <p className="text-sm text-secondary-600 dark:text-secondary-300">{text ?? children}</p>
);

// Button is wired to the action handler injected by the renderer.
export function makeButton(onAction: ActionHandler): React.FC<{ label: string; action?: string; variant?: string }> {
  const Button: React.FC<{ label: string; action?: string; variant?: string }> = ({ label, action, variant }) => (
    <button
      onClick={() => action && onAction(action)}
      className={
        variant === 'primary'
          ? 'inline-flex w-fit items-center gap-1.5 rounded-lg bg-secondary-900 dark:bg-white px-3 py-1.5 text-sm font-medium text-white dark:text-secondary-900 hover:bg-black dark:hover:bg-secondary-100'
          : 'inline-flex w-fit items-center gap-1.5 rounded-lg border border-secondary-300 dark:border-secondary-600 px-3 py-1.5 text-sm font-medium text-secondary-700 dark:text-secondary-200 hover:bg-secondary-50 dark:hover:bg-secondary-800'
      }
    >
      <LayoutGrid className="h-4 w-4" />
      {label}
    </button>
  );
  return Button;
}

// ── Intent-driven widgets (see CLAUDE.md → Agent Response UI Conventions) ──

// choices → inline tappable chips (single tap = send; multi = toggle then send).
// Not a dropdown; dark-mode adaptive. Optional — the user can ignore it and keep chatting.
const CHIP = 'px-3 py-1.5 rounded-full border text-sm transition-colors';
const CHIP_OFF = 'border-secondary-300 dark:border-secondary-600 text-secondary-800 dark:text-secondary-100 hover:bg-secondary-100 dark:hover:bg-secondary-800';
const CHIP_ON = 'border-secondary-900 dark:border-white bg-secondary-900 dark:bg-white text-white dark:text-secondary-900';

export function makeSelect(onAction: ActionHandler): React.FC<{ label?: string; options: Array<{ label: string; value: string }>; action?: string; multiple?: boolean }> {
  const Choices: React.FC<{ label?: string; options: Array<{ label: string; value: string }>; action?: string; multiple?: boolean }> = ({ label, options = [], action, multiple }) => {
    const [picked, setPicked] = React.useState<string[]>([]);
    const toggle = (v: string) => setPicked((p) => (p.includes(v) ? p.filter((x) => x !== v) : [...p, v]));
    return (
      <div className="mt-1 space-y-2">
        {label && <p className="text-sm text-secondary-600 dark:text-secondary-300">{label}</p>}
        <div className="flex flex-wrap gap-2">
          {options.map((o) =>
            multiple ? (
              <button key={o.value} type="button" onClick={() => toggle(o.value)}
                className={`${CHIP} ${picked.includes(o.value) ? CHIP_ON : CHIP_OFF}`}>{o.label}</button>
            ) : (
              <button key={o.value} type="button" onClick={() => action && onAction(action, o.value)}
                className={`${CHIP} ${CHIP_OFF}`}>{o.label}</button>
            )
          )}
        </div>
        {multiple && (
          <button type="button" disabled={!picked.length}
            onClick={() => action && onAction(action, picked.join(', '))}
            className="text-xs font-medium px-3 py-1.5 rounded-lg bg-secondary-900 dark:bg-white text-white dark:text-secondary-900 disabled:opacity-40">
            Send{picked.length ? ` (${picked.length})` : ''}
          </button>
        )}
      </div>
    );
  };
  return Choices;
}

// bullets → a structured list as its own element (distinct from prose markdown).
// Items can be plain strings; with `action` set they become tappable (tap = next turn).
type BulletItemT = { text: string; value?: string };
export function makeBullets(onAction: ActionHandler): React.FC<{ title?: string; ordered?: boolean; action?: string; items?: Array<string | BulletItemT> }> {
  const Bullets: React.FC<{ title?: string; ordered?: boolean; action?: string; items?: Array<string | BulletItemT> }> = ({ title, ordered, action, items = [] }) => {
    const norm = items.map((it) => (typeof it === 'string' ? { text: it } : it));
    const List = (ordered ? 'ol' : 'ul') as 'ol' | 'ul';
    return (
      <div>
        {title && <div className="mb-1 text-xs font-medium uppercase tracking-wide text-secondary-400">{title}</div>}
        <List className={`${ordered ? 'list-decimal' : 'list-disc'} pl-5 space-y-1 text-sm text-secondary-700 dark:text-secondary-200 marker:text-secondary-400`}>
          {norm.map((it, i) => (
            <li key={i}>
              {action ? (
                <button type="button" onClick={() => onAction(action, it.value ?? it.text)}
                  className="text-left hover:text-secondary-900 dark:hover:text-white hover:underline">{it.text}</button>
              ) : it.text}
            </li>
          ))}
        </List>
      </div>
    );
  };
  return Bullets;
}

// list with details → Table
export const Table: React.FC<{ title?: string; columns: string[]; rows: Array<Array<string | number>> }> = ({ title, columns = [], rows = [] }) => (
  <div>
    {title && <div className="mb-1 text-xs font-medium uppercase tracking-wide text-secondary-400">{title}</div>}
    <div className="overflow-x-auto rounded-lg border border-secondary-200 dark:border-secondary-700">
      <table className="w-full text-sm">
        <thead className="bg-secondary-50 dark:bg-secondary-800 text-left text-secondary-500 dark:text-secondary-300">
          <tr>{columns.map((c, i) => <th key={i} className="px-3 py-2 font-medium">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-secondary-100 dark:border-secondary-700">{r.map((cell, j) => <td key={j} className="px-3 py-2 text-secondary-700 dark:text-secondary-200">{cell}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ChartFrame: React.FC<{ title?: string; children: React.ReactElement }> = ({ title, children }) => (
  // `text-secondary-*` sets `currentColor` for the chart ink (theme-aware).
  <div className="text-secondary-900 dark:text-secondary-100">
    {title && <div className="mb-1 text-xs font-medium uppercase tracking-wide text-secondary-400">{title}</div>}
    <div style={{ width: '100%', height: 220 }}><ResponsiveContainer>{children}</ResponsiveContainer></div>
  </div>
);

// Shared theme-aware axis/grid props (currentColor at low opacity reads in both themes).
const AXIS = { tick: { fill: 'currentColor', fillOpacity: 0.7, fontSize: 12 }, stroke: 'currentColor', strokeOpacity: 0.3 } as const;
const GRID = { stroke: 'currentColor', strokeOpacity: 0.12 } as const;

// segment / share of whole → PieChart
export const PieChart: React.FC<{ title?: string; data: Array<{ name: string; value: number }> }> = ({ title, data = [] }) => (
  <ChartFrame title={title}>
    <RPie>
      <Pie data={data} dataKey="value" nameKey="name" outerRadius={80} label isAnimationActive={false}>
        {data.map((_, i) => <Cell key={i} fill="currentColor" fillOpacity={rampAt(i)} />)}
      </Pie>
      <Tooltip /><Legend />
    </RPie>
  </ChartFrame>
);

// trend over time → LineChart
export const LineChart: React.FC<{ title?: string; xKey: string; lines: Array<{ key: string; label?: string }>; data: Array<Record<string, any>> }> = ({ title, xKey, lines = [], data = [] }) => (
  <ChartFrame title={title}>
    <RLine data={data}>
      <CartesianGrid strokeDasharray="3 3" {...GRID} /><XAxis dataKey={xKey} {...AXIS} /><YAxis {...AXIS} /><Tooltip /><Legend />
      {lines.map((l, i) => <Line key={l.key} type="monotone" dataKey={l.key} name={l.label || l.key} stroke="currentColor" strokeOpacity={rampAt(i)} isAnimationActive={false} />)}
    </RLine>
  </ChartFrame>
);

// comparison of categories → BarChart
export const BarChart: React.FC<{ title?: string; xKey: string; bars: Array<{ key: string; label?: string }>; data: Array<Record<string, any>> }> = ({ title, xKey, bars = [], data = [] }) => (
  <ChartFrame title={title}>
    <RBar data={data}>
      <CartesianGrid strokeDasharray="3 3" {...GRID} /><XAxis dataKey={xKey} {...AXIS} /><YAxis {...AXIS} /><Tooltip /><Legend />
      {bars.map((b, i) => <Bar key={b.key} dataKey={b.key} name={b.label || b.key} fill="currentColor" fillOpacity={rampAt(i)} isAnimationActive={false} />)}
    </RBar>
  </ChartFrame>
);

// clustering check → ScatterChart
export const ScatterChart: React.FC<{ title?: string; xKey?: string; yKey?: string; points: Array<{ x: number; y: number; group?: string }> }> = ({ title, points = [] }) => (
  <ChartFrame title={title}>
    <RScatter>
      <CartesianGrid strokeDasharray="3 3" {...GRID} /><XAxis type="number" dataKey="x" {...AXIS} /><YAxis type="number" dataKey="y" {...AXIS} /><Tooltip />
      <Scatter data={points} fill="currentColor" fillOpacity={rampAt(0)} isAnimationActive={false} />
    </RScatter>
  </ChartFrame>
);

// probability distribution (bell curve) → uncertainty / benchmarking. Display-only.
// Agent gives mean + stdDev per series; we compute the Gaussian density here.
type DistSeries = { label?: string; mean: number; stdDev: number };
type DistMarker = { x: number; label?: string };
const gaussian = (x: number, mean: number, sd: number) =>
  (1 / (sd * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * ((x - mean) / sd) ** 2);

export const Distribution: React.FC<{ title?: string; xLabel?: string; series?: DistSeries[]; markers?: DistMarker[] }> = ({ title, xLabel, series = [], markers = [] }) => {
  const valid = series.filter((s) => s && s.stdDev > 0);
  if (!valid.length) return null;
  const lo = Math.min(...valid.map((s) => s.mean - 4 * s.stdDev));
  const hi = Math.max(...valid.map((s) => s.mean + 4 * s.stdDev));
  const N = 60;
  const keyOf = (s: DistSeries, i: number) => s.label || `Series ${i + 1}`;
  const data = Array.from({ length: N + 1 }, (_, i) => {
    const x = lo + (i / N) * (hi - lo);
    const row: Record<string, number> = { x: Math.round(x * 100) / 100 };
    valid.forEach((s, si) => { row[keyOf(s, si)] = gaussian(x, s.mean, s.stdDev); });
    return row;
  });
  return (
    <ChartFrame title={title}>
      <RLine data={data}>
        <CartesianGrid strokeDasharray="3 3" {...GRID} />
        <XAxis dataKey="x" type="number" domain={[lo, hi]} tickCount={6} {...AXIS}
          label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -2 } : undefined} />
        <YAxis width={16} tick={false} axisLine={false} />
        <Tooltip /><Legend />
        {valid.map((s, i) => (
          <Line key={i} type="monotone" dataKey={keyOf(s, i)} name={keyOf(s, i)} dot={false} stroke="currentColor" strokeOpacity={rampAt(i)} isAnimationActive={false} />
        ))}
        {markers.map((m, i) => (
          <ReferenceLine key={`m${i}`} x={m.x} stroke="currentColor" strokeOpacity={0.5} strokeDasharray="4 2"
            label={m.label ? { value: m.label, position: 'top', fontSize: 11 } : undefined} />
        ))}
      </RLine>
    </ChartFrame>
  );
};

// downloadable artifact (deck/summary/diagram) → a card with a Download button that
// fires the action handler ('artifact:download' + id). Produced by the tooling layer.
const ARTIFACT_ICON: Record<string, React.ReactNode> = {
  summary: <FileText className="h-5 w-5 text-slate-600 dark:text-secondary-300" />,
  diagram: <Workflow className="h-5 w-5 text-slate-600 dark:text-secondary-300" />,
  deck: <Presentation className="h-5 w-5 text-slate-600 dark:text-secondary-300" />,
};
export function makeArtifact(onAction: ActionHandler): React.FC<{ artifactId: string; kind?: string; filename?: string; mimeType?: string }> {
  const Artifact: React.FC<{ artifactId: string; kind?: string; filename?: string }> = ({ artifactId, kind = 'summary', filename = 'artifact' }) => (
    <div className="mt-1 flex items-center gap-3 rounded-xl border border-slate-200 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-3 py-2.5">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 dark:bg-secondary-800 shrink-0">
        {ARTIFACT_ICON[kind] || ARTIFACT_ICON.summary}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-secondary-900 dark:text-white">{filename}</p>
        <p className="text-xs capitalize text-secondary-400">{kind}</p>
      </div>
      <button
        type="button"
        onClick={() => onAction('artifact:download', artifactId)}
        className="inline-flex items-center gap-1.5 rounded-lg bg-secondary-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-black dark:bg-white dark:text-secondary-900 dark:hover:bg-secondary-100"
      >
        <Download className="h-3.5 w-3.5" /> Download
      </button>
    </div>
  );
  return Artifact;
}

// ── Analyst artifacts (the Interrogate step) ────────────────────────
// InlineCard supplies the outer card chrome (see CARD_TYPES), so these render content only —
// mirroring how Table/charts are structured. Monochrome, Lucide-only, dark-mode aware.

type LensPoint = { text: string; by?: string };

const STANCE_ICON: Record<string, React.ReactNode> = {
  bull: <TrendingUp className="h-3.5 w-3.5" />,
  bear: <TrendingDown className="h-3.5 w-3.5" />,
  neutral: <Minus className="h-3.5 w-3.5" />,
};

// A labelled list of lens-attributed points (shared by the thesis one-pager sections).
const LensList: React.FC<{ label: string; Icon: React.FC<{ className?: string }>; items?: LensPoint[] }> = ({ label, Icon, items = [] }) => {
  if (!items.length) return null;
  return (
    <div>
      <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-secondary-500 dark:text-secondary-400">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-secondary-700 dark:text-secondary-200 leading-snug">
            {it.text}
            {it.by && <span className="text-secondary-400 dark:text-secondary-500"> &mdash; {it.by}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
};

// thesis under test → claim + directional stance/confidence, the case for, the case against, what to watch.
export const ThesisOnePager: React.FC<{
  title?: string; thesis?: string; stance?: string; confidence?: number;
  drivers?: LensPoint[]; risks?: LensPoint[]; watch?: LensPoint[];
}> = ({ title, thesis, stance, confidence, drivers = [], risks = [], watch = [] }) => {
  const pct = typeof confidence === 'number' ? Math.max(0, Math.min(100, confidence)) : null;
  return (
    <div className="space-y-3">
      {title && <div className="text-xs font-medium uppercase tracking-wide text-secondary-400">{title}</div>}
      <div className="rounded-lg bg-secondary-50 dark:bg-secondary-800/60 p-3">
        {thesis && <p className="text-sm font-medium leading-snug text-secondary-900 dark:text-white">{thesis}</p>}
        {(stance || pct != null) && (
          <div className="mt-2 flex flex-wrap items-center gap-3">
            {stance && (
              <span className="inline-flex items-center gap-1 rounded-full border border-secondary-300 dark:border-secondary-600 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-secondary-600 dark:text-secondary-300">
                {STANCE_ICON[stance] || STANCE_ICON.neutral} {stance}
              </span>
            )}
            {pct != null && (
              <span className="flex items-center gap-1.5 text-[11px] text-secondary-500 dark:text-secondary-400">
                <span className="inline-block h-1.5 w-16 overflow-hidden rounded-full bg-secondary-200 dark:bg-secondary-700">
                  <span className="block h-full bg-secondary-500 dark:bg-secondary-300" style={{ width: `${pct}%` }} />
                </span>
                {Math.round(pct)}% confidence
              </span>
            )}
          </div>
        )}
      </div>
      <LensList label="Drivers" Icon={TrendingUp} items={drivers} />
      <LensList label="Risks" Icon={AlertTriangle} items={risks} />
      <LensList label="What to watch" Icon={Eye} items={watch} />
    </div>
  );
};

// forward paths → a compact matrix (scenario · probability · outcome · implication).
type ScenarioRowT = { name: string; probability?: number; outcome?: string; drivers?: string; implication?: string };
export const ScenarioMatrix: React.FC<{ title?: string; scenarios?: ScenarioRowT[] }> = ({ title, scenarios = [] }) => (
  <div>
    {title && <div className="mb-1 text-xs font-medium uppercase tracking-wide text-secondary-400">{title}</div>}
    <div className="overflow-x-auto rounded-lg border border-secondary-200 dark:border-secondary-700">
      <table className="w-full text-sm">
        <thead className="bg-secondary-50 dark:bg-secondary-800 text-left text-secondary-500 dark:text-secondary-300">
          <tr>
            <th className="px-3 py-2 font-medium">Scenario</th>
            <th className="px-3 py-2 font-medium">Prob.</th>
            <th className="px-3 py-2 font-medium">Outcome</th>
            <th className="px-3 py-2 font-medium">Implication</th>
          </tr>
        </thead>
        <tbody>
          {scenarios.map((s, i) => (
            <tr key={i} className="border-t border-secondary-100 dark:border-secondary-700 align-top">
              <td className="px-3 py-2 font-medium text-secondary-900 dark:text-white">
                <span className="whitespace-nowrap">{s.name}</span>
                {s.drivers && <span className="mt-0.5 block text-xs font-normal text-secondary-400">{s.drivers}</span>}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-secondary-700 dark:text-secondary-200">{typeof s.probability === 'number' ? `${Math.round(s.probability)}%` : '—'}</td>
              <td className="px-3 py-2 text-secondary-700 dark:text-secondary-200">{s.outcome || '—'}</td>
              <td className="px-3 py-2 text-secondary-700 dark:text-secondary-200">{s.implication || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

/** Build the catalog (components needing the action handler are injected). */
export function buildCatalog(onAction: ActionHandler): Record<string, React.FC<any>> {
  return {
    Stack, Card, StatGroup, Stat, TaskList, Heading, Text,
    Button: makeButton(onAction),
    Select: makeSelect(onAction),
    Bullets: makeBullets(onAction),
    Artifact: makeArtifact(onAction),
    Table, PieChart, LineChart, BarChart, ScatterChart, Distribution,
    ThesisOnePager, ScenarioMatrix,
  };
}
